import { useMemo } from 'react'

// The grower → farm → block picker derivation: distinct growers from a blocks
// list, the farms under the picked grower, and the blocks under the picked
// farm. Solves the "80+ blocks in one flat dropdown" problem everywhere a
// farm page plans/records per-block work.
//
// Mirrors the real hierarchy (mig 20260701134112_puc_farms_layer):
//
//   grower → puc → puc_farm → block
//
// PUC is deliberately skipped as a picker step: blocks_sync_puc_from_farm_trg
// force-syncs blocks.puc_id from the farm's PUC, so picking a farm already
// determines the PUC. Surfacing both would be two dropdowns for one choice.
//
// A step with exactly one option auto-selects and hides itself — see
// showGrowerStep / showFarmStep. On a FARM tenant the grower step is always
// the one-option case (a farm tenant has exactly one master grower row), so
// this collapses the picker to farm → block there without a per-page branch.

// Blocks may carry a null puc_farm_id — the column is nullable even though the
// mig backfilled every row. They land in a synthetic "Unassigned" farm rather
// than being filtered out of the picker entirely.
export const UNASSIGNED_FARM_ID = '__unassigned__'

export type GrowerFarmBlockRow = {
  grower_id: string
  grower_name: string
  puc_farm_id: string | null
  puc_farm_name: string | null
}

type Option = { id: string; name: string }

export function useGrowerFarmBlockFilter<T extends GrowerFarmBlockRow>(
  blocks: T[],
  growerId: string,
  farmId: string,
): {
  growers: Option[]
  farms: Option[]
  filteredBlocks: T[]
  /** The grower actually in effect — the picked one, or the sole option. */
  effectiveGrowerId: string
  /** The farm actually in effect — the picked one, or the sole option. */
  effectiveFarmId: string
  showGrowerStep: boolean
  showFarmStep: boolean
} {
  const growers = useMemo(() => dedupe(blocks, b => [b.grower_id, b.grower_name]), [blocks])

  // One grower → auto-select it. Derived rather than written back into the
  // caller's state, so it can't race the modals that seed growerId from an
  // edited row or a deep-link prefill.
  const effectiveGrowerId = growers.length === 1 ? growers[0].id : growerId

  const growerBlocks = useMemo(
    () => effectiveGrowerId ? blocks.filter(b => b.grower_id === effectiveGrowerId) : [],
    [blocks, effectiveGrowerId],
  )

  const farms = useMemo(
    () => dedupe(growerBlocks, b => [
      b.puc_farm_id ?? UNASSIGNED_FARM_ID,
      b.puc_farm_name ?? 'Unassigned',
    ]),
    [growerBlocks],
  )

  const effectiveFarmId = farms.length === 1 ? farms[0].id : farmId

  const filteredBlocks = useMemo(
    () => effectiveFarmId
      ? growerBlocks.filter(b => (b.puc_farm_id ?? UNASSIGNED_FARM_ID) === effectiveFarmId)
      : [],
    [growerBlocks, effectiveFarmId],
  )

  return {
    growers,
    farms,
    filteredBlocks,
    effectiveGrowerId,
    effectiveFarmId,
    showGrowerStep: growers.length > 1,
    showFarmStep: farms.length > 1,
  }
}

// A block's text in a picker or a filter select — the part that tells two
// blocks apart FIRST (farm audit X-20 / SC-06). A native <select> shows one
// line and cuts the rest, and the old "Grower · PUC · Block A11 — …" order
// spent that line on the one thing every option shared: on a phone the
// register filter read "Sonskyn Boerdery (Pty) Ltd · W1234 · Blo…" and the
// scout could not tell A1 from A10, or see which block the page showed.
//
//   A11 (Midknight Valencia) · Citrus · W1234 · Sonskyn Boerdery
//   └ name └ variety           └ commodity   └ PUC  └ grower
//
// - variety: skipped when the block's name already spells it ("14B Nadorcott").
// - commodity: only with `withCommodity` — the growth-stage picker needs it
//   (its stage list is per commodity); a register filter does not.
// - grower: only with `showGrower` — pass `spansGrowers(blocks)`, the same test
//   that shows the picker's grower step. A FARM tenant has one grower, so its
//   blocks never carry it; a list spanning growers still does.
// - mapBlockScope's '—' placeholders (PUC, grower) are dropped, not printed.
export interface BlockLabelRow {
  block_name:   string
  variety?:     string | null
  commodity?:   string | null
  puc_code?:    string | null
  grower_id?:   string | null
  grower_name?: string | null
}

// In a select, render <BlockOptions blocks={…}/> (src/components/BlockOptions)
// rather than calling this — it carries the spansGrowers test with it.
export function blockOptionLabel(
  b: BlockLabelRow,
  showGrower: boolean,
  withCommodity = false,
): string {
  const named = !!b.variety && b.block_name.toLowerCase().includes(b.variety.toLowerCase())
  const parts = [b.variety && !named ? `${b.block_name} (${b.variety})` : b.block_name]
  if (withCommodity && b.commodity) parts.push(b.commodity)
  if (b.puc_code && b.puc_code !== '—') parts.push(b.puc_code)
  if (showGrower && b.grower_name && b.grower_name !== '—') parts.push(b.grower_name)
  return parts.join(' · ')
}

/** True when a block list spans more than one grower — the only case where a
 *  block's label needs the grower's name (see blockOptionLabel). Keyed on the
 *  id when the row carries one, else the name. A missing grower (mapBlockScope's
 *  '—', a report's '' placeholder) is not a second grower. */
export function spansGrowers(
  blocks: ReadonlyArray<{ grower_id?: string | null; grower_name?: string | null }>,
): boolean {
  const seen = new Set<string>()
  for (const b of blocks) {
    const key = b.grower_id || b.grower_name
    if (key && key !== '—') seen.add(key)
    if (seen.size > 1) return true
  }
  return false
}

// Distinct (id, name) pairs in first-wins order, sorted by name.
function dedupe<T>(rows: T[], pick: (row: T) => [string, string]): Option[] {
  const seen = new Map<string, string>()
  for (const row of rows) {
    const [id, name] = pick(row)
    if (id && !seen.has(id)) seen.set(id, name)
  }
  return Array.from(seen.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

// The scope fields every farm picker needs off a `blocks` row embedded as
// `pucs:puc_id ( puc_code, grower_id, growers:grower_id ( name ) )` +
// `puc_farms:puc_farm_id ( farm_name )`. Spread it and add whatever else the
// page selected:
//   .map((r: any) => ({ ...mapBlockScope(r), hectares: r.hectares ?? null }))
// Eight fields of null-coalescing is the whole job; splitting it to get under
// the CRAP threshold would just scatter the defaults it exists to centralise.
// fallow-ignore-next-line complexity
export function mapBlockScope(r: any): GrowerFarmBlockRow & {
  block_id: string; block_name: string; variety: string | null; puc_code: string
} {
  return {
    block_id:      r.block_id,
    block_name:    r.block_name,
    variety:       r.variety,
    puc_code:      r.pucs?.puc_code  ?? '—',
    grower_id:     r.pucs?.grower_id ?? '',
    grower_name:   r.pucs?.growers?.name ?? '—',
    puc_farm_id:   r.puc_farm_id,
    puc_farm_name: r.puc_farms?.farm_name ?? null,
  }
}

// Seed the picker from an existing row's block — edit mode, or a deep-link
// prefill. Pair them in the page's lazy useState initializers:
//   const [growerId, setGrowerId] = useState(() => seedGrowerId(blocks, target?.block_id))
//   const [farmId,   setFarmId]   = useState(() => seedFarmId(blocks, target?.block_id))
export function seedGrowerId<T extends GrowerFarmBlockRow & { block_id: string }>(
  blocks: T[], blockId: string | null | undefined,
): string {
  return findBlock(blocks, blockId)?.grower_id ?? ''
}

export function seedFarmId<T extends GrowerFarmBlockRow & { block_id: string }>(
  blocks: T[], blockId: string | null | undefined,
): string {
  const b = findBlock(blocks, blockId)
  return b ? b.puc_farm_id ?? UNASSIGNED_FARM_ID : ''
}

function findBlock<T extends { block_id: string }>(
  blocks: T[], blockId: string | null | undefined,
): T | undefined {
  return blockId ? blocks.find(b => b.block_id === blockId) : undefined
}

// The distinct growers alone, for pages that want a grower *filter* dropdown
// rather than the picker (FarmCompliance / FarmEstimates / the Phenology
// list header). Previously these called the picker hook with an empty
// growerId and discarded the block half.
export function useDistinctGrowers(
  blocks: Array<{ grower_id: string; grower_name: string }>,
): Option[] {
  return useMemo(() => dedupe(blocks, b => [b.grower_id, b.grower_name]), [blocks])
}
